/**
 * What: Canonical value inventories for Shopify Shopper Background levers.
 * Transport / data-flow: apps/shopify derives lever values from these; apps/web
 * keys prompt instructions by them and guard-tests full cue coverage.
 */
/** Allocation complement bucket. Never rendered into a persona prompt. */
export const OTHER_CUSTOMERS_BUCKET_LABEL = 'other tracked customers';
export const SHOPIFY_CUSTOMER_GROUP_MIN_VISIBLE_COUNT = 5;
/** Canonical whole-store lifecycle allocation shared by Customer Insights and study setup. */
export function buildShopifyCustomerLifecycleMixDistribution(input) {
    if (input.totalCustomers <= 0)
        return {};
    const visible = input.groups.filter((group) => group.count >= SHOPIFY_CUSTOMER_GROUP_MIN_VISIBLE_COUNT);
    const exactShares = visible.map((group) => ({
        label: group.label,
        exact: (group.count * 100) / input.totalCustomers,
    }));
    const distribution = {};
    let assigned = 0;
    for (const share of exactShares) {
        const floored = Math.floor(share.exact);
        distribution[share.label] = floored;
        assigned += floored;
    }
    const visibleExactTotal = exactShares.reduce((total, share) => total + share.exact, 0);
    const remainderShare = Math.max(0, Math.round(100 - visibleExactTotal));
    let roundingDust = 100 - assigned - remainderShare;
    for (const share of [...exactShares].sort((left, right) => (right.exact % 1) - (left.exact % 1) || left.label.localeCompare(right.label))) {
        if (roundingDust <= 0)
            break;
        distribution[share.label] += 1;
        roundingDust -= 1;
    }
    if (remainderShare > 0)
        distribution[OTHER_CUSTOMERS_BUCKET_LABEL] = remainderShare;
    return distribution;
}
export const ORDER_COUNT_BAND_LABELS = ['1 order', '2 orders', '3-5 orders', '6+ orders'];
export const SPEND_BAND_LABELS = ['$0-$99', '$100-$249', '$250-$499', '$500+'];
/** Merchant-facing merges of the 11 official CustomerRfmGroup values. All 11 must map. */
export const SHOPIFY_RFM_MERGED_GROUPS = [
    { label: 'Best customers', shopifyGroups: ['CHAMPIONS', 'LOYAL'] },
    { label: 'Steady shoppers', shopifyGroups: ['ACTIVE', 'NEEDS_ATTENTION'] },
    { label: 'Drifting away', shopifyGroups: ['AT_RISK', 'ALMOST_LOST'] },
    { label: 'Gone quiet', shopifyGroups: ['DORMANT', 'PREVIOUSLY_LOYAL'] },
    { label: 'Just arrived', shopifyGroups: ['NEW', 'PROMISING'] },
    { label: 'Never bought', shopifyGroups: ['PROSPECTS'] },
];
/** CustomerRfmGroup inventory from Shopify Admin GraphQL API 2025-07. */
export const SHOPIFY_CUSTOMER_RFM_GROUP_VALUES = SHOPIFY_RFM_MERGED_GROUPS.flatMap((group) => group.shopifyGroups).sort();
/** CustomerPredictedSpendTier inventory from Shopify Admin GraphQL API 2025-07. */
export const SHOPIFY_CUSTOMER_PREDICTED_SPEND_TIER_VALUES = ['HIGH', 'LOW', 'MEDIUM'];
const SHOPIFY_CUSTOMER_RFM_GROUP_VALUE_SET = new Set(SHOPIFY_CUSTOMER_RFM_GROUP_VALUES);
const SHOPIFY_CUSTOMER_PREDICTED_SPEND_TIER_VALUE_SET = new Set(SHOPIFY_CUSTOMER_PREDICTED_SPEND_TIER_VALUES);
/** Keeps only official CustomerRfmGroup values at persistence boundaries. */
export function normalizeShopifyCustomerRfmGroup(value) {
    return typeof value === 'string' && SHOPIFY_CUSTOMER_RFM_GROUP_VALUE_SET.has(value)
        ? value
        : null;
}
/** Keeps only official CustomerPredictedSpendTier values at persistence boundaries. */
export function normalizeShopifyCustomerPredictedSpendTier(value) {
    return typeof value === 'string' && SHOPIFY_CUSTOMER_PREDICTED_SPEND_TIER_VALUE_SET.has(value)
        ? value
        : null;
}
const RFM_VALUE_TO_LABEL = new Map(SHOPIFY_RFM_MERGED_GROUPS.flatMap((group) => group.shopifyGroups.map((value) => [value, group.label])));
/** Maps an official rfmGroup enum value to its merchant-facing merged label, or null. */
export function mergedRfmGroupLabel(rfmGroup) {
    if (!rfmGroup)
        return null;
    return RFM_VALUE_TO_LABEL.get(rfmGroup.trim().toUpperCase()) ?? null;
}
const RFM_MERGED_GROUP_LABELS = SHOPIFY_RFM_MERGED_GROUPS.map((group) => group.label);
/** Field name of the audience frame (picker selection); distinct from the marginal lever field. */
export const SHOPIFY_RFM_GROUP_FRAME_FIELD = 'shopify_rfm_group_frame';
/** Lever id of the whole-store-mix frame. */
export const SHOPIFY_RFM_GROUP_FRAME_MIX_ID = `${SHOPIFY_RFM_GROUP_FRAME_FIELD}:mix`;
/** Boolean Shopify conditioning fields whose true and false values both shape prompt context. */
export const SHOPIFY_BOOLEAN_CONDITIONING_FIELDS = [
    'shopify_high_value_customer',
    'shopify_repeat_customer',
    'shopify_lapsed_customer',
    'shopify_product_affinity_customer',
    'shopify_discount_sensitive_customer',
    'shopify_custom_merchant_segment',
];
/** Static value-keyed conditioning fields: every value listed here must carry a written cue. */
export const SHOPIFY_CONDITIONING_VALUE_INVENTORY = {
    shopify_high_value_customer: ['true', 'false'],
    shopify_repeat_customer: ['true', 'false'],
    shopify_lapsed_customer: ['true', 'false'],
    shopify_product_affinity_customer: ['true', 'false'],
    shopify_discount_sensitive_customer: ['true', 'false'],
    shopify_custom_merchant_segment: ['true', 'false'],
    shopify_rfm_group: RFM_MERGED_GROUP_LABELS,
    // The audience FRAME (picker selection: one group at 100%, or the whole-store mix).
    // Distinct field so a frame can never be confused with the marginal lever above.
    // Shares the marginal field's label array by construction, not coincidence.
    shopify_rfm_group_frame: RFM_MERGED_GROUP_LABELS,
    shopify_order_count_band: ORDER_COUNT_BAND_LABELS,
    shopify_spend_band: SPEND_BAND_LABELS,
};
/** Dynamic-valued fields: cues are generated per value at render time. */
export const SHOPIFY_DYNAMIC_VALUE_FIELDS = ['shopify_product_affinity_tag'];
/** Narrow-free membership check so callers with plain strings avoid tuple-includes friction. */
export function isShopifyDynamicValueField(field) {
    return SHOPIFY_DYNAMIC_VALUE_FIELDS.includes(field);
}
//# sourceMappingURL=shopper-background-values.js.map